/**
 * Property Detail Page
 * 부동산 상세 페이지 - 호주 특화 정보 표시
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Property } from "@shared/schemas/properties";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  AlertCircle,
  ArrowLeft,
  Bath,
  Bed,
  Building2,
  Calendar,
  Car,
  Dog,
  DollarSign,
  Eye,
  Flame,
  Heart,
  Home,
  MapPin,
  MessageCircle,
  Shield,
  Sofa,
  Wifi,
  Wind,
  Share2,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Info,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";
import { AgentContactForm } from "@/components/AgentContactForm";
import { PropertyMap } from "@/components/property/PropertyMap";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function PropertyDetail() {
  const [match, params] = useRoute<{ id: string }>("/properties/:id");
  const [, setLocation] = useLocation();
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const { user, isAdmin } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-6xl mx-auto px-4">
          <Card className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">
              유효하지 않은 매물 ID입니다
            </p>
            <Button
              variant="outline"
              onClick={() => setLocation("/properties")}
            >
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const propertyId = params.id;

  // BusinessDirectoryDetail 패턴 사용: queryClient의 기본 queryFn이 자동으로 unwrapPayload 처리
  // API는 snake_case로 반환하므로 any 타입 사용
  const { data: propertyData, isLoading, error } = useQuery<any>({
    queryKey: [`/api/properties/${propertyId}`],
    enabled: !!propertyId,
  });

  // 디버깅: API 응답 확인
  if (propertyData) {
    console.log('[PropertyDetail] propertyData:', propertyData);
  }
  if (error) {
    console.error('[PropertyDetail] error:', error);
  }

  // 상태 변경 mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const { getIdToken } = await import("@/lib/firebase");
      const token = await getIdToken();
      const res = await fetch(`/api/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "상태 변경에 실패했습니다");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/properties/${propertyId}`] });
      toast({
        title: "상태가 변경되었습니다",
        description: "매물 상태가 성공적으로 업데이트되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "오류 발생",
        description: error.message || "상태 변경에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  // Property History 쿼리
  const { data: historyData } = useQuery<any>({
    queryKey: ["/api/properties", propertyId, "history"],
    queryFn: async () => {
      const res = await fetch(`/api/properties/${propertyId}/history`);
      if (!res.ok) return { history: [] };
      const json = await res.json();
      return json.ok && json.data ? json.data : json;
    },
    enabled: !!propertyId && showHistoryDialog,
  });

  // API 응답을 Property 타입으로 변환 (queryClient가 이미 언래핑함)
  // API는 snake_case로 반환하므로 직접 접근
  const property: any = propertyData || null;

  if (isLoading || !propertyData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-96 bg-muted rounded-lg" />
            <div className="h-32 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-2xl font-bold mb-2">
          매물 정보를 찾을 수 없습니다
        </h2>
        <Button onClick={() => setLocation("/properties")} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          목록으로 돌아가기
        </Button>
      </div>
    );
  }

  const isOwner = user?.uid === property?.user_id;
  const canEditStatus = isOwner || isAdmin;

  // 이미지 배열 생성 (primary_image + images)
  const allImages: string[] = [];
  if (property?.primary_image) {
    allImages.push(property.primary_image);
  }
  if (property?.images && Array.isArray(property.images)) {
    property.images.forEach((img) => {
      if (img && !allImages.includes(img)) {
        allImages.push(img);
      }
    });
  }

  const formatPrice = (
    cents: number | null | undefined,
    period?: string | null
  ) => {
    if (cents === null || cents === undefined) return "가격 문의";
    if (cents === 0) return "무료";
    const price = Math.round(cents / 100);
    if (period === "weekly") {
      return `$${price.toLocaleString()} per week`;
    } else if (period === "monthly") {
      return `$${price.toLocaleString()} per month`;
    }
    return `$${price.toLocaleString()}`;
  };

  const nextImage = () => {
    if (allImages.length > 0) {
      setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
    }
  };

  const prevImage = () => {
    if (allImages.length > 0) {
      setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    }
  };

  return (
    <PageContainer size="default">
      {/* 상단 액션 버튼 */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          onClick={() => setLocation("/properties")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          목록으로
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon">
            <Share2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon">
            <Bookmark className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Property Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* 주소 및 기본 정보 */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                {property.address || `${property.suburb || property.city}, ${property.state}`}
              </CardTitle>
              {/* 호주 표준 형식: 3/2/2 (침실/욕실/주차) with icons */}
              <div className="flex items-center gap-2 mt-2 text-lg font-semibold">
                <div className="flex items-center gap-1">
                  <Bed className="h-5 w-5 text-muted-foreground" />
                  {property.bedrooms !== null && property.bedrooms !== undefined ? (
                    <span>{property.bedrooms}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
                <span className="text-muted-foreground">/</span>
                <div className="flex items-center gap-1">
                  <Bath className="h-5 w-5 text-muted-foreground" />
                  {property.bathrooms !== null && property.bathrooms !== undefined ? (
                    <span>{property.bathrooms}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
                <span className="text-muted-foreground">/</span>
                <div className="flex items-center gap-1">
                  <Car className="h-5 w-5 text-muted-foreground" />
                  {property?.parking_spaces !== null && property?.parking_spaces !== undefined ? (
                    <span>{property.parking_spaces}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </div>
                <span className="text-muted-foreground ml-3 capitalize text-sm font-normal">
                  {property?.listing_type}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 가격 정보 */}
              <div className="space-y-2">
                <p className="text-primary text-3xl font-bold">
                  {formatPrice(property?.price, property?.price_period)}
                </p>
                {property?.bond_amount && property?.property_type === "rent" && (
                  <p className="text-muted-foreground">
                    Bond ${Math.round(property.bond_amount / 100).toLocaleString()}
                  </p>
                )}
                {property?.available_from && (
                  <p className="text-sm text-muted-foreground">
                    Available {property.available_from === "now" ? "now" : `from ${property.available_from}`}
                  </p>
                )}
              </div>

              <Separator />

              {/* Property History */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Info className="w-4 h-4" />
                <button
                  onClick={() => setShowHistoryDialog(true)}
                  className="text-primary hover:underline text-left"
                >
                  Property history
                </button>
              </div>

              <Separator />

              {/* Financial Tools (렌트인 경우) */}
              {property?.property_type === "rent" && (
                <>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Have you considered buying?</p>
                    <button
                      onClick={() => setLocation(`/tools/rent-or-buy?price=${property?.price}&period=${property?.price_period || "weekly"}`)}
                      className="text-sm text-primary hover:underline text-left"
                    >
                      Rent or buy calculator →
                    </button>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">First home buyer's checklist</p>
                    <button
                      onClick={() => setLocation(`/tools/borrowing-calculator?price=${property?.price}`)}
                      className="text-sm text-primary hover:underline text-left"
                    >
                      Find out how much you can borrow →
                    </button>
                  </div>
                  <Separator />
                </>
              )}

              {/* 지도 (향후 구현) */}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  위치
                </h3>
                <p className="text-muted-foreground">
                  {property.state} {property.city}
                  {property.suburb && `, ${property.suburb}`}
                </p>
                {property.address && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {property.address}
                  </p>
                )}
                <div className="mt-4">
                  <PropertyMap
                    lat={property.latitude}
                    lng={property.longitude}
                    address={property?.address}
                    city={property?.city}
                    suburb={property?.suburb}
                    state={property?.state}
                  />
                </div>
              </div>

              <Separator />

              {/* Property Highlights */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold">Property highlights</h3>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  AI-generated from property description. Please verify all information.
                </p>
                <div className="flex flex-wrap gap-2">
                  {property.furnished && (
                    <Badge variant="outline">
                      <Sofa className="w-3 h-3 mr-1" />
                      가구 포함
                    </Badge>
                  )}
                  {property.petAllowed && (
                    <Badge variant="outline">
                      <Dog className="w-3 h-3 mr-1" />
                      반려동물 허용
                    </Badge>
                  )}
                  {property.billsIncluded && (
                    <Badge variant="outline">
                      <DollarSign className="w-3 h-3 mr-1" />
                      Bills Included
                    </Badge>
                  )}
                  {property.nbnAvailable && (
                    <Badge variant="outline">
                      <Wifi className="w-3 h-3 mr-1" />
                      NBN
                    </Badge>
                  )}
                  {property.aircon && (
                    <Badge variant="outline">
                      <Wind className="w-3 h-3 mr-1" />
                      Aircon
                    </Badge>
                  )}
                  {property.heating && (
                    <Badge variant="outline">
                      <Flame className="w-3 h-3 mr-1" />
                      Heating
                    </Badge>
                  )}
                  {property.leaseTermMonths && (
                    <Badge variant="outline">
                      Fixed {property.leaseTermMonths}-Month Lease Term
                    </Badge>
                  )}
                </div>
              </div>

              <Separator />

              {property.description && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-2">상세 설명</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {property.description}
                    </p>
                  </div>
                </>
              )}

              <Separator />
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  위치
                </h3>
                <p className="text-muted-foreground">
                  {property.state} {property.city}
                  {property.suburb && `, ${property.suburb}`}
                </p>
                {property.address && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {property.address}
                  </p>
                )}
              </div>

              {/* 호주 특화 정보 */}
              {(property.inspectionDate ||
                property.availableFrom ||
                property.leaseTermMonths) && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      호주 특화 정보
                    </h3>
                    <div className="space-y-2">
                      {property.inspectionDate && (() => {
                        try {
                          const inspectionDate = new Date(property.inspectionDate);
                          if (isNaN(inspectionDate.getTime())) {
                            return (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Inspection: {property.inspectionDate}
                                  {property.inspectionTime && ` ${property.inspectionTime}`}
                                </Badge>
                                {!isOwner && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setLocation(`/properties/${property.id}/inspection`)}
                                  >
                                    예약하기
                                  </Button>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Inspection: {inspectionDate.toLocaleDateString("ko-KR", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                                {property.inspectionTime && ` ${property.inspectionTime}`}
                              </Badge>
                              {!isOwner && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setLocation(`/properties/${property.id}/inspection`)}
                                >
                                  예약하기
                                </Button>
                              )}
                            </div>
                          );
                        } catch (error) {
                          return (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Inspection: {property.inspectionDate}
                                {property.inspectionTime && ` ${property.inspectionTime}`}
                              </Badge>
                              {!isOwner && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setLocation(`/properties/${property.id}/inspection`)}
                                >
                                  예약하기
                                </Button>
                              )}
                            </div>
                          );
                        }
                      })()}
                      {property.availableFrom && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            Available From: {property.availableFrom}
                          </span>
                        </div>
                      )}
                      {property.leaseTermMonths && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            Lease Term: {property.leaseTermMonths} months
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* 안전 정보 박스 */}
              <Separator />
              <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                        안전 거래 안내
                      </h4>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <li>• 계약서를 반드시 작성하고 확인하세요</li>
                        <li>• Bond는 정부 기관에 보관되어야 합니다</li>
                        <li>• Inspection 시 주의사항을 확인하세요</li>
                        <li>• 사기 의심 시 즉시 신고하세요</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Separator />
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>조회 {property.views}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Heart className="w-4 h-4" />
                  <span>찜 {property.favorites}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    등록{" "}
                    {(() => {
                      try {
                        if (!property.createdAt) return "알 수 없음";
                        const createdAt = new Date(property.createdAt);
                        if (isNaN(createdAt.getTime())) return property.createdAt;
                        return formatDistanceToNow(createdAt, {
                          addSuffix: true,
                          locale: ko,
                        });
                      } catch (error) {
                        return property.createdAt || "알 수 없음";
                      }
                    })()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Images & Agent Section */}
        <div className="lg:col-span-1 space-y-6">
          {/* 메인 이미지 */}
          <Card>
            <CardContent className="p-0">
              {allImages.length > 0 ? (
                <>
                  <div className="relative aspect-video bg-muted overflow-hidden rounded-t-lg">
                    <img
                      src={allImages[selectedImageIndex]}
                      alt={property?.listing_type || "Property"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}
                    {property?.is_premium && (
                      <Badge className="absolute top-2 left-2 bg-yellow-500 text-white">
                        프리미엄
                      </Badge>
                    )}
                  </div>
                  {allImages.length > 1 && (
                    <div className="p-4 flex gap-2 overflow-x-auto">
                      {allImages.slice(0, 3).map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 ${
                            idx === selectedImageIndex
                              ? "border-primary"
                              : "border-transparent"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`${property?.listing_type || "Property"} ${idx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e5e7eb" width="200" height="200"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="16" dy="105" dx="100" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </button>
                      ))}
                      {allImages.length > 3 && (
                        <div className="flex-shrink-0 w-20 h-20 rounded-md bg-muted flex items-center justify-center border-2 border-transparent">
                          <span className="text-xs font-semibold">+{allImages.length - 3}</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-video flex items-center justify-center bg-muted rounded-t-lg">
                  <Home className="w-24 h-24 text-muted-foreground" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* 에이전트 섹션 */}
          {property?.is_agent_listing ? (
            <AgentContactForm
              property={property}
              agentName={property.sellerDisplayName || property.sellerNickname || undefined}
              businessName={property.businessName || undefined}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  판매자 정보
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <Avatar>
                    <AvatarImage src="" />
                    <AvatarFallback>
                      {property.sellerDisplayName?.[0] ||
                        property.sellerNickname?.[0] ||
                        "?"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">
                      {property.sellerDisplayName ||
                        property.sellerNickname ||
                        "익명"}
                    </p>
                  </div>
                </div>
                {!isOwner && (
                  <Button className="w-full" size="lg">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    문의하기
                  </Button>
                )}
                {(isOwner || isAdmin) && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">매물 상태</p>
                      <Select
                        value={property?.status || "available"}
                        onValueChange={(value) => updateStatusMutation.mutate(value)}
                        disabled={updateStatusMutation.isPending}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="available">판매/임대 중</SelectItem>
                          <SelectItem value="reserved">예약됨</SelectItem>
                          {property?.property_type === "sale" ? (
                            <SelectItem value="sold">판매완료</SelectItem>
                          ) : (
                            <SelectItem value="rented">계약완료</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Property History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Property History</DialogTitle>
            <DialogDescription>
              가격 및 상태 변경 이력을 확인하세요
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {historyData?.history && historyData.history.length > 0 ? (
              <div className="space-y-3">
                {historyData.history.map((item: any, index: number) => {
                  const formatValue = (value: string | null, fieldName: string) => {
                    if (!value) return "N/A";
                    if (fieldName === "price") {
                      return new Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD",
                        minimumFractionDigits: 0,
                      }).format(parseInt(value) / 100);
                    }
                    if (fieldName === "status") {
                      const statusMap: Record<string, string> = {
                        available: "Available",
                        reserved: "Reserved",
                        sold: "Sold",
                        rented: "Rented",
                      };
                      return statusMap[value] || value;
                    }
                    return value;
                  };

                  const fieldNameMap: Record<string, string> = {
                    price: "Price",
                    status: "Status",
                    inspection_date: "Inspection Date",
                    inspection_time: "Inspection Time",
                    bond_amount: "Bond Amount",
                    lease_term_months: "Lease Term",
                    available_from: "Available From",
                    price_type: "Price Type",
                    price_period: "Price Period",
                  };

                  const date = new Date(item.created_at);
                  const isValidDate = !isNaN(date.getTime());

                  return (
                    <Card key={item.id || index}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">
                                {fieldNameMap[item.field_name] || item.field_name}
                              </Badge>
                              {isValidDate && (
                                <span className="text-xs text-muted-foreground">
                                  {date.toLocaleDateString("en-AU", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">From:</span>
                                <span className="text-sm font-medium">
                                  {formatValue(item.old_value, item.field_name)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">To:</span>
                                <span className="text-sm font-semibold text-primary">
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Info className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>변경 이력이 없습니다</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
